import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ClipboardCheck, Download, ShieldCheck, ShieldOff, Eye, RefreshCw } from "lucide-react";
import { api } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import { Button } from "@/components/ui/button";

export default function AccessReview() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/auth/access-review");
      setData(res.data);
    } catch { toast.error("Could not load access review"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const exportCsv = async () => {
    try {
      const res = await api.get("/auth/access-review/export", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "access-review.csv"; a.click();
      URL.revokeObjectURL(url);
      toast.success("Access review exported");
    } catch { toast.error("Export failed"); }
  };

  if (!data) return <div className="p-6 font-mono text-sm text-muted-foreground">Loading access review…</div>;

  return (
    <div className="p-6 space-y-6 max-w-6xl" data-testid="access-review-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-head font-black text-3xl tracking-tight flex items-center gap-2">
            <ClipboardCheck className="w-7 h-7 text-cyan-400" /> Access Review
          </h1>
          <p className="text-sm text-muted-foreground font-mono mt-1">
            Who has access, 2FA status, last login, and recent PHI record views · generated {fmtDateTime(data.generated_at)}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={load} className="gap-2 border-white/15 h-9" data-testid="access-refresh"><RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /></Button>
          <Button onClick={exportCsv} className="gap-2 bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold h-9" data-testid="access-export-btn"><Download className="w-4 h-4" /> Export CSV</Button>
        </div>
      </div>

      <section className="glass rounded-xl p-4" data-testid="access-users-card">
        <h2 className="font-head font-bold text-lg mb-3">Users &amp; access</h2>
        <div className="overflow-x-auto rounded-lg border border-white/8">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[11px] uppercase tracking-wide text-muted-foreground font-mono border-b border-white/8">
                <th className="text-left px-3 py-2">User</th>
                <th className="text-left px-2 py-2">Role</th>
                <th className="text-left px-2 py-2">Status</th>
                <th className="text-left px-2 py-2">2FA</th>
                <th className="text-left px-2 py-2">Last login</th>
                <th className="text-right px-2 py-2">PHI views (30d)</th>
                <th className="text-left px-3 py-2">Last access</th>
              </tr>
            </thead>
            <tbody>
              {data.users.map((u) => (
                <tr key={u.email} className="border-b border-white/5 last:border-0" data-testid={`access-user-${u.email}`}>
                  <td className="px-3 py-2">
                    <div className="font-medium">{u.name || "—"}</div>
                    <div className="text-xs text-muted-foreground font-mono">{u.email}</div>
                  </td>
                  <td className="px-2 py-2 font-mono text-xs uppercase text-cyan-300">{u.role}</td>
                  <td className="px-2 py-2 text-xs">{u.active ? <span className="text-emerald-300">active</span> : <span className="text-red-300">inactive</span>}</td>
                  <td className="px-2 py-2 text-xs">
                    {u.mfa_enabled
                      ? <span className="inline-flex items-center gap-1 text-emerald-300"><ShieldCheck className="w-3.5 h-3.5" />{u.recovery_remaining} codes</span>
                      : <span className="inline-flex items-center gap-1 text-amber-300"><ShieldOff className="w-3.5 h-3.5" />no 2FA</span>}
                  </td>
                  <td className="px-2 py-2 text-xs font-mono text-muted-foreground">{u.last_login_at ? fmtDateTime(u.last_login_at) : "never"}</td>
                  <td className="px-2 py-2 text-right font-mono tnum text-xs">{u.phi_views_30d}</td>
                  <td className="px-3 py-2 text-xs font-mono text-muted-foreground">{u.last_access_at ? fmtDateTime(u.last_access_at) : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="glass rounded-xl p-4" data-testid="access-recent-card">
        <h2 className="font-head font-bold text-lg mb-3 flex items-center gap-2"><Eye className="w-5 h-5 text-violet-400" /> Recent record views</h2>
        {data.recent_access.length === 0 && <p className="text-xs text-muted-foreground font-mono">No record views logged yet.</p>}
        <div className="space-y-1">
          {data.recent_access.map((a, i) => (
            <div key={i} className="flex items-center justify-between text-xs border-b border-white/5 last:border-0 py-1.5" data-testid={`access-view-${i}`}>
              <span className="font-mono text-cyan-300 w-24 shrink-0">{a.referral_id}</span>
              <span className="flex-1 truncate px-2">{a.user_email} <span className="text-muted-foreground">({a.role})</span></span>
              <span className={`text-[11px] font-mono px-1.5 rounded border ${a.phi_exposed ? "text-amber-300 border-amber-500/40" : "text-slate-400 border-slate-500/40"}`}>{a.phi_exposed ? "PHI" : "redacted"}</span>
              <span className="font-mono text-muted-foreground w-40 text-right shrink-0">{fmtDateTime(a.at)}</span>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
